/** Contexto do resort/empreendimento para mensagens de compartilhamento. */
export interface ShareResortContext {
  name?: string;
  city?: string;
  state?: string;
  description?: string;
}

/** Remove HTML, entidades e caracteres de controle — texto plano para WhatsApp/copia. */
export function sanitizeShareText(text: string, maxLength = 150): string {
  if (!text) return '';

  let s = text
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/[\u0000-\u001F\u007F-\u009F\u200B-\u200D\uFEFF]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  if (maxLength > 0 && s.length > maxLength) {
    s = `${s.slice(0, maxLength - 3).trim()}...`;
  }
  return s;
}

/** Hashtag ASCII (sem acentos nem espacos). */
export function toShareHashtag(value: string): string {
  const base = value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9]+/g, '');
  return base.length > 0 ? base : '';
}

export function buildPropertyShareMessage(params: {
  title: string;
  propertyDescription?: string;
  resort?: ShareResortContext;
  fullUrl: string;
}): string {
  const { title, propertyDescription, resort, fullUrl } = params;
  const safeTitle = sanitizeShareText(title, 120);

  const propertySnippet = sanitizeShareText(propertyDescription || '', 120);
  const resortSnippet = sanitizeShareText(resort?.description || '', 120);
  const body =
    propertySnippet ||
    resortSnippet ||
    'Conheca este imovel na LiveStay.';

  const resortName = sanitizeShareText(resort?.name || '', 80);
  const city = sanitizeShareText(resort?.city || '', 60);
  const state = sanitizeShareText(resort?.state || '', 10);

  let locationLine = 'Imovel disponivel na LiveStay.';
  if (resortName && city && state) {
    locationLine = `Imovel no ${resortName} em ${city}, ${state}.`;
  } else if (resortName && city) {
    locationLine = `Imovel no ${resortName} em ${city}.`;
  } else if (resortName) {
    locationLine = `Imovel no ${resortName}.`;
  } else if (city && state) {
    locationLine = `Imovel em ${city}, ${state}.`;
  }

  const tags = ['LiveStay', 'Hospedagem'];
  const resortTag = resortName ? toShareHashtag(resortName) : '';
  const cityTag = city ? toShareHashtag(city) : '';
  if (resortTag) tags.push(resortTag);
  if (cityTag && cityTag !== resortTag) tags.push(cityTag);

  const hashtagLine = tags.map((t) => `#${t}`).join(' ');

  return [
    safeTitle,
    '',
    body,
    '',
    locationLine,
    '',
    fullUrl,
    '',
    hashtagLine,
  ].join('\n');
}

export function buildPartnerShareMessage(params: {
  title: string;
  description?: string;
  fullUrl: string;
}): string {
  const safeTitle = sanitizeShareText(params.title, 120);
  const body =
    sanitizeShareText(params.description || '', 120) ||
    'Parceiro oficial na LiveStay.';

  return [
    safeTitle,
    '',
    body,
    '',
    params.fullUrl,
    '',
    '#LiveStay #Parceiros',
  ].join('\n');
}
